import React, { useState } from 'react';
import AdminSidebar from './components/AdminSidebar';
import Header from '../teacher_screen/components/Header';
import {
    Users,
    CheckSquare,
    MoreHorizontal,
    Search,
    ChevronDown,
    Filter,
    Download,
    PieChart,
    Calendar,
    ArrowUpRight,
    GraduationCap,
    Clock
} from 'lucide-react';

export default function AdminAttendance() {
    const attendanceSummary = [
        { grade: 'Grade 8', total: 180, present: 172, percentage: '95.5%', status: 'Stable' },
        { grade: 'Grade 9', total: 165, present: 140, percentage: '84.8%', status: 'Low' },
        { grade: 'Grade 10', total: 210, present: 205, percentage: '97.6%', status: 'Optimal' },
        { grade: 'Grade 11', total: 145, present: 138, percentage: '95.1%', status: 'Stable' },
        { grade: 'Grade 12', total: 120, present: 110, percentage: '91.6%', status: 'Watch' },
    ];

    return (
        <div className="flex h-screen bg-[#F8FAFC] overflow-hidden font-sans">
            <AdminSidebar />

            <main className="flex-1 overflow-y-auto px-8">
                <Header
                    title="Attendance Audit"
                    subtitle="Institutional presence tracking and absentee analytics"
                >
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-[14px] font-black text-gray-700 shadow-sm cursor-pointer hover:bg-gray-50 uppercase tracking-tight">
                        <Calendar size={18} className="text-blue-500" />
                        <span>March 07, 2026</span>
                    </div>

                    <button className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-[14px] font-bold text-gray-700 shadow-sm hover:bg-gray-50 uppercase tracking-tight">
                        <Download size={18} className="text-gray-400" />
                        Report
                    </button>
                </Header>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10 overflow-hidden">
                    <div className="bg-[#111827] rounded-[32px] p-8 text-white flex flex-col justify-between h-full min-h-[300px]">
                        <div>
                            <p className="text-blue-400 text-[11px] font-black uppercase tracking-[0.2em] mb-4">Real-time Presence</p>
                            <h3 className="text-[36px] font-black tracking-tighter mb-1">94.8% Active Now</h3>
                            <p className="text-gray-400 text-[14px] leading-relaxed">System recorded 1,213 students currently on premises across all sectors.</p>
                        </div>
                        <div className="flex items-center gap-6 pt-8 border-t border-white/10 mt-8">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-emerald-500 rounded-lg"><CheckSquare size={16} /></div>
                                <div>
                                    <p className="text-[18px] font-black">1,150</p>
                                    <p className="text-[10px] font-bold text-gray-500 uppercase">On Time</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-amber-500 rounded-lg"><Clock size={16} /></div>
                                <div>
                                    <p className="text-[18px] font-black">63</p>
                                    <p className="text-[10px] font-bold text-gray-500 uppercase">Late Entry</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-[32px] border border-gray-100 shadow-sm p-8 flex flex-col justify-center">
                        <div className="flex items-center justify-between mb-8">
                            <h3 className="text-[18px] font-black text-[#111827] uppercase tracking-tight">Absentee Requests</h3>
                            <span className="px-3 py-1 bg-rose-50 text-rose-500 rounded-lg text-[10px] font-black tracking-widest uppercase">12 Pending Action</span>
                        </div>
                        <div className="space-y-4">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-gray-50 hover:bg-blue-50/50 transition-all cursor-pointer">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-full bg-white border border-gray-100 flex items-center justify-center font-black text-[12px] text-blue-500 shadow-sm">
                                            S
                                        </div>
                                        <div>
                                            <p className="text-[14px] font-black text-[#111827]">Michael Scott</p>
                                            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-tight">Grade 10B • Sick Leave</p>
                                        </div>
                                    </div>
                                    <ArrowUpRight size={18} className="text-gray-300" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden flex flex-col mb-10 font-sans">
                    <div className="p-8 flex items-center justify-between border-b border-gray-50">
                        <h3 className="text-[20px] font-black text-[#111827]">Attendance Summary By Grade</h3>
                        <div className="flex items-center gap-4">
                            <span className="text-[12px] font-bold text-gray-400">Viewing March 2026 data</span>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-[#F8FAFC]">
                                <tr>
                                    <th className="px-8 py-5 text-[11px] font-black text-gray-400 tracking-widest uppercase">Grade / Section</th>
                                    <th className="px-6 py-5 text-[11px] font-black text-gray-400 tracking-widest uppercase text-center">Enrolled</th>
                                    <th className="px-6 py-5 text-[11px] font-black text-gray-400 tracking-widest uppercase text-center">Present</th>
                                    <th className="px-6 py-5 text-[11px] font-black text-gray-400 tracking-widest uppercase text-center">Avg Presence</th>
                                    <th className="px-8 py-5 text-[11px] font-black text-gray-400 tracking-widest uppercase text-center">Status</th>
                                    <th className="px-8 py-5 text-[11px] font-black text-gray-400 tracking-widest uppercase text-right">Audit</th>
                                </tr>
                            </thead>
                            <tbody>
                                {attendanceSummary.map((v, i) => (
                                    <tr key={i} className="border-b border-gray-50 last:border-0 hover:bg-slate-50 transition-all group">
                                        <td className="px-8 py-5 text-[15px] font-black text-[#111827] uppercase tracking-tight">{v.grade}</td>
                                        <td className="px-6 py-5 text-center text-[14px] font-bold text-gray-700">{v.total}</td>
                                        <td className="px-6 py-5 text-center text-[14px] font-bold text-gray-500">{v.present}</td>
                                        <td className="px-6 py-5">
                                            <div className="flex flex-col gap-1.5 items-center">
                                                <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                    <div
                                                        style={{ width: v.percentage }}
                                                        className={`h-full rounded-full ${v.percentage.replace('%', '') > 90 ? 'bg-emerald-500' : v.percentage.replace('%', '') > 80 ? 'bg-blue-500' : 'bg-rose-500'}`}
                                                    ></div>
                                                </div>
                                                <span className="text-[12px] font-black text-gray-700">{v.percentage}</span>
                                            </div>
                                        </td>
                                        <td className="px-8 py-5 text-center">
                                            <span className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${v.status === 'Optimal' ? 'text-emerald-500 bg-emerald-50' :
                                                    v.status === 'Stable' ? 'text-blue-500 bg-blue-50' :
                                                        v.status === 'Watch' ? 'text-amber-500 bg-amber-50' : 'text-rose-500 bg-rose-50'
                                                }`}>
                                                {v.status}
                                            </span>
                                        </td>
                                        <td className="px-8 py-5 text-right">
                                            <button className="p-2 text-gray-300 hover:text-gray-900 transition-all"><MoreHorizontal size={20} /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>
        </div>
    );
}
